
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Event {
  id: number;
  title: {
    en: string;
    ar: string;
  };
  date: {
    en: string;
    ar: string;
  };
  location: {
    en: string;
    ar: string;
  };
  category: string;
  attendees: number;
  image: string;
  startTime?: {
    en: string;
    ar: string;
  };
  endTime?: {
    en: string;
    ar: string;
  };
  remainingDays?: number;
  remainingHours?: number;
  remainingMinutes?: number;
  xSpaceUrl?: string;
  description?: {
    en: string;
    ar: string;
  };
  isLive?: boolean;
}

interface EventsContextType {
  events: Event[];
  addEvent: (event: Event) => void;
  removeEvent: (id: number) => void;
  updateEvent: (updatedEvent: Event) => void;
}

const EventsContext = createContext<EventsContextType | undefined>(undefined);

const STORAGE_KEY = 'saudi_events_data';

const defaultEvents: Event[] = [
    {
      id: 1,
      title: {
        en: "Tech Conference 2024",
        ar: "مؤتمر التقنية 2024"
      },
      date: {
        en: "March 15-16, 2024",
        ar: "15-16 مارس 2024"
      },
      location: {
        en: "Riyadh, Saudi Arabia",
        ar: "الرياض، المملكة العربية السعودية"
      },
      category: "Technology",
      attendees: 1200,
      image: "https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9",
      startTime: {
        en: "9:00 AM",
        ar: "9:00 صباحاً"
      },
      endTime: {
        en: "6:00 PM",
        ar: "6:00 مساءً"
      },
      remainingDays: 15,
      remainingHours: 8,
      remainingMinutes: 45
    },
    {
      id: 2,
      title: {
        en: "Box Games Store Opening Soon",
        ar: "افتتاح متجر جيم بوكس قريبا"
      },
      date: {
        en: "April 5-6, 2024",
        ar: "5-6 أبريل 2024"
      },
      location: {
        en: "Jeddah, Saudi Arabia",
        ar: "جدة، المملكة العربية السعودية"
      },
      category: "Applications",
      attendees: 800,
      image: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05",
      startTime: {
        en: "10:00 AM",
        ar: "10:00 صباحاً"
      },
      endTime: {
        en: "10:00 PM",
        ar: "10:00 مساءً"
      },
      remainingDays: 35,
      remainingHours: 12,
      remainingMinutes: 20
    },
    {
      id: 3,
      title: {
        en: "Public Services Platform",
        ar: "منصة تخليص للخدمات العامة"
      },
      date: {
        en: "May 20, 2024",
        ar: "20 مايو 2024"
      },
      location: {
        en: "Dammam, Saudi Arabia",
        ar: "الدمام، المملكة العربية السعودية"
      },
      category: "Applications",
      attendees: 1500,
      image: "https://images.unsplash.com/photo-1500375592092-40eb2168fd21",
      startTime: {
        en: "8:00 AM",
        ar: "8:00 صباحاً"
      },
      endTime: {
        en: "4:00 PM",
        ar: "4:00 مساءً"
      },
      remainingDays: 60,
      remainingHours: 16,
      remainingMinutes: 30
    },
    {
      id: 4,
      title: {
        en: "Umrah Spring 2025 Activities",
        ar: "فعاليات العمرة الربيع 2025"
      },
      date: {
        en: "February 17-19, 2025",
        ar: "17-19 فبراير 2025"
      },
      location: {
        en: "Makkah, Saudi Arabia",
        ar: "مكة المكرمة، المملكة العربية السعودية"
      },
      category: "Religious",
      attendees: 5000,
      image: "https://images.unsplash.com/photo-1482938289607-e9573fc25ebb",
      startTime: {
        en: "6:00 AM",
        ar: "6:00 صباحاً"
      },
      endTime: {
        en: "10:00 PM",
        ar: "10:00 مساءً"
      },
      remainingDays: 300,
      remainingHours: 22,
      remainingMinutes: 15
    },
    {
      id: 5,
      title: {
        en: "Saudi Food Exhibition",
        ar: "معرض الأغذية السعودي"
      },
      date: {
        en: "February 4-8, 2024",
        ar: "4-8 فبراير 2024"
      },
      location: {
        en: "Riyadh, Saudi Arabia",
        ar: "الرياض، المملكة العربية السعودية"
      },
      category: "Exhibition",
      attendees: 3000,
      image: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07",
      startTime: {
        en: "10:00 AM",
        ar: "10:00 صباحاً"
      },
      endTime: {
        en: "10:00 PM",
        ar: "10:00 مساءً"
      },
      remainingDays: 5,
      remainingHours: 14,
      remainingMinutes: 30
    },
    {
      id: 6,
      title: {
        en: "Saudi International Medical Conference",
        ar: "المؤتمر الطبي السعودي الدولي"
      },
      date: {
        en: "February 11-13, 2024",
        ar: "11-13 فبراير 2024"
      },
      location: {
        en: "Riyadh, Saudi Arabia",
        ar: "الرياض، المملكة العربية السعودية"
      },
      category: "Medical",
      attendees: 2500,
      image: "https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9",
      startTime: {
        en: "9:00 AM",
        ar: "9:00 صباحاً"
      },
      endTime: {
        en: "6:00 PM",
        ar: "6:00 مساءً"
      },
      remainingDays: 12,
      remainingHours: 8,
      remainingMinutes: 45
    },
    {
      id: 7,
      title: {
        en: "Saudi Entertainment and Tourism Exhibition",
        ar: "معرض الترفيه والسياحة السعودي"
      },
      date: {
        en: "February 18-22, 2024",
        ar: "18-22 فبراير 2024"
      },
      location: {
        en: "Jeddah, Saudi Arabia",
        ar: "جدة، المملكة العربية السعودية"
      },
      category: "Tourism",
      attendees: 4000,
      image: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05",
      startTime: {
        en: "4:00 PM",
        ar: "4:00 مساءً"
      },
      endTime: {
        en: "11:00 PM",
        ar: "11:00 مساءً"
      },
      remainingDays: 19,
      remainingHours: 10,
      remainingMinutes: 15
    },
    // Conferences & Seminars
    {
      id: 8,
      title: {
        en: "Digital Transformation Summit",
        ar: "قمة التحول الرقمي"
      },
      date: {
        en: "March 10-12, 2024",
        ar: "10-12 مارس 2024"
      },
      location: {
        en: "Riyadh, Saudi Arabia",
        ar: "الرياض، المملكة العربية السعودية"
      },
      category: "Conferences",
      attendees: 1800,
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87",
      startTime: { en: "9:00 AM", ar: "9:00 صباحاً" },
      endTime: { en: "5:00 PM", ar: "5:00 مساءً" },
      remainingDays: 25,
      remainingHours: 6,
      remainingMinutes: 30
    },
    {
      id: 9,
      title: {
        en: "AI & Future Technologies Conference",
        ar: "مؤتمر الذكاء الاصطناعي وتقنيات المستقبل"
      },
      date: {
        en: "April 15-17, 2024",
        ar: "15-17 أبريل 2024"
      },
      location: {
        en: "Jeddah, Saudi Arabia",
        ar: "جدة، المملكة العربية السعودية"
      },
      category: "Conferences",
      attendees: 2200,
      image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678",
      startTime: { en: "8:00 AM", ar: "8:00 صباحاً" },
      endTime: { en: "6:00 PM", ar: "6:00 مساءً" },
      remainingDays: 45,
      remainingHours: 10,
      remainingMinutes: 15
    },
    {
      id: 10,
      title: {
        en: "Leadership & Management Seminar",
        ar: "ندوة القيادة والإدارة"
      },
      date: {
        en: "May 5, 2024",
        ar: "5 مايو 2024"
      },
      location: {
        en: "Dammam, Saudi Arabia",
        ar: "الدمام، المملكة العربية السعودية"
      },
      category: "Conferences",
      attendees: 500,
      image: "https://images.unsplash.com/photo-1475721027785-f74eccf877e2",
      startTime: { en: "10:00 AM", ar: "10:00 صباحاً" },
      endTime: { en: "4:00 PM", ar: "4:00 مساءً" },
      remainingDays: 55,
      remainingHours: 8,
      remainingMinutes: 45
    },
    // Exhibitions
    {
      id: 11,
      title: {
        en: "Saudi Auto Show 2024",
        ar: "معرض السيارات السعودي 2024"
      },
      date: {
        en: "March 20-25, 2024",
        ar: "20-25 مارس 2024"
      },
      location: {
        en: "Riyadh, Saudi Arabia",
        ar: "الرياض، المملكة العربية السعودية"
      },
      category: "Exhibitions",
      attendees: 15000,
      image: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d",
      startTime: { en: "10:00 AM", ar: "10:00 صباحاً" },
      endTime: { en: "10:00 PM", ar: "10:00 مساءً" },
      remainingDays: 30,
      remainingHours: 14,
      remainingMinutes: 20
    },
    {
      id: 12,
      title: {
        en: "Saudi Book Fair",
        ar: "معرض الكتاب السعودي"
      },
      date: {
        en: "April 1-10, 2024",
        ar: "1-10 أبريل 2024"
      },
      location: {
        en: "Riyadh, Saudi Arabia",
        ar: "الرياض، المملكة العربية السعودية"
      },
      category: "Exhibitions",
      attendees: 50000,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d",
      startTime: { en: "9:00 AM", ar: "9:00 صباحاً" },
      endTime: { en: "11:00 PM", ar: "11:00 مساءً" },
      remainingDays: 40,
      remainingHours: 12,
      remainingMinutes: 0
    },
    {
      id: 13,
      title: {
        en: "Saudi Construction Expo",
        ar: "معرض البناء السعودي"
      },
      date: {
        en: "May 15-18, 2024",
        ar: "15-18 مايو 2024"
      },
      location: {
        en: "Jeddah, Saudi Arabia",
        ar: "جدة، المملكة العربية السعودية"
      },
      category: "Exhibitions",
      attendees: 8000,
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd",
      startTime: { en: "10:00 AM", ar: "10:00 صباحاً" },
      endTime: { en: "8:00 PM", ar: "8:00 مساءً" },
      remainingDays: 70,
      remainingHours: 6,
      remainingMinutes: 30
    },
    // Public Occasions
    {
      id: 14,
      title: {
        en: "Saudi National Day Celebration",
        ar: "احتفالات اليوم الوطني السعودي"
      },
      date: {
        en: "September 23, 2024",
        ar: "23 سبتمبر 2024"
      },
      location: {
        en: "All Cities, Saudi Arabia",
        ar: "جميع المدن، المملكة العربية السعودية"
      },
      category: "PublicOccasions",
      attendees: 100000,
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96",
      startTime: { en: "4:00 PM", ar: "4:00 مساءً" },
      endTime: { en: "12:00 AM", ar: "12:00 صباحاً" },
      remainingDays: 200,
      remainingHours: 20,
      remainingMinutes: 0
    },
    {
      id: 15,
      title: {
        en: "Founding Day Celebration",
        ar: "احتفالات يوم التأسيس"
      },
      date: {
        en: "February 22, 2024",
        ar: "22 فبراير 2024"
      },
      location: {
        en: "Riyadh, Saudi Arabia",
        ar: "الرياض، المملكة العربية السعودية"
      },
      category: "PublicOccasions",
      attendees: 75000,
      image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3",
      startTime: { en: "5:00 PM", ar: "5:00 مساءً" },
      endTime: { en: "11:00 PM", ar: "11:00 مساءً" },
      remainingDays: 22,
      remainingHours: 10,
      remainingMinutes: 30
    },
    {
      id: 16,
      title: {
        en: "Eid Al-Fitr Celebration",
        ar: "احتفالات عيد الفطر"
      },
      date: {
        en: "April 10-13, 2024",
        ar: "10-13 أبريل 2024"
      },
      location: {
        en: "All Cities, Saudi Arabia",
        ar: "جميع المدن، المملكة العربية السعودية"
      },
      category: "PublicOccasions",
      attendees: 200000,
      image: "https://images.unsplash.com/photo-1564769625905-50e93615e769",
      startTime: { en: "6:00 AM", ar: "6:00 صباحاً" },
      endTime: { en: "11:00 PM", ar: "11:00 مساءً" },
      remainingDays: 50,
      remainingHours: 8,
      remainingMinutes: 15
    },
    // X Spaces
    {
      id: 17,
      title: {
        en: "Entrepreneurship X Space",
        ar: "مساحة ريادة الأعمال"
      },
      date: {
        en: "February 5, 2024",
        ar: "5 فبراير 2024"
      },
      location: {
        en: "Online",
        ar: "عبر الإنترنت"
      },
      category: "XSpaces",
      attendees: 5000,
      image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113",
      startTime: { en: "8:00 PM", ar: "8:00 مساءً" },
      endTime: { en: "10:00 PM", ar: "10:00 مساءً" },
      remainingDays: 5,
      remainingHours: 12,
      remainingMinutes: 0,
      xSpaceUrl: "https://twitter.com/i/spaces/example1",
      description: {
        en: "Join us for a live discussion about entrepreneurship and startups",
        ar: "انضم إلينا لحوار مباشر حول ريادة الأعمال والشركات الناشئة"
      }
    },
    {
      id: 18,
      title: {
        en: "Tech Talk X Space",
        ar: "حوار تقني على منصة اكس"
      },
      date: {
        en: "February 12, 2024",
        ar: "12 فبراير 2024"
      },
      location: {
        en: "Online",
        ar: "عبر الإنترنت"
      },
      category: "XSpaces",
      attendees: 3000,
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3",
      startTime: { en: "9:00 PM", ar: "9:00 مساءً" },
      endTime: { en: "11:00 PM", ar: "11:00 مساءً" },
      xSpaceUrl: "https://twitter.com/i/spaces/example2",
      description: {
        en: "Live tech discussions with industry experts",
        ar: "حوارات تقنية مباشرة مع خبراء الصناعة"
      },
      remainingDays: 12,
      remainingHours: 16,
      remainingMinutes: 30
    }
];

export function EventsProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<Event[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading events from localStorage:', error);
    }
    return defaultEvents;
  });

  // حفظ التغييرات في localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
    } catch (error) {
      console.error('Error saving events to localStorage:', error);
    }
  }, [events]);

  const addEvent = (event: Event) => {
    setEvents(prevEvents => [...prevEvents, event]);
  };

  const removeEvent = (id: number) => {
    setEvents(prevEvents => prevEvents.filter(event => event.id !== id));
  };

  const updateEvent = (updatedEvent: Event) => {
    setEvents(prevEvents => 
      prevEvents.map(event => event.id === updatedEvent.id ? updatedEvent : event)
    );
  };

  return (
    <EventsContext.Provider value={{ events, addEvent, removeEvent, updateEvent }}>
      {children}
    </EventsContext.Provider>
  );
}

export function useEvents() {
  const context = useContext(EventsContext);
  if (context === undefined) {
    throw new Error('useEvents must be used within an EventsProvider');
  }
  return context;
}
